/**
 * Ad Manager - Gestione sistema pubblicitario
 * 
 * Supporta preroll, midroll, postroll con sorgenti static e GAM/VAST.
 * Sistema modulare ed espandibile per future sorgenti ads.
 * 
 * @since v3.0.0 - Nuovo sistema ads modulare
 */

import { AD_TYPES, AD_SOURCES, AD_STATES, AD_EVENTS, POSITION_TYPES, DEFAULT_FREQUENCY_CAP } from '../constants/adTypes.js';
import { VASTParser } from '../parsers/VASTParser.js';
import { generateId } from '../utils/format.js';
import { dispatchCustomEvent } from '../utils/events.js';

export class AdManager {
  /**
   * Costruttore
   * @param {Array} adsConfig - Array configurazione ads
   * @param {Object} options - Opzioni (frequencyCapping, stateManager, etc.)
   */
  constructor(adsConfig = [], options = {}) {
    this.adsConfig = this.parseAndValidateAds(adsConfig);
    this.frequencyCap = { ...DEFAULT_FREQUENCY_CAP, ...options.frequencyCapping };
    this.stateManager = options.stateManager; // Opzionale, per sincronizzare adsPlayed
    
    // Stato ads
    this.playedAds = new Set(); // IDs ads già riprodotte (in-memory)
    this.currentAd = null;
    this.adAudioElement = null;
    
    // VAST Parser
    this.vastParser = new VASTParser();
    
    // Midroll points calcolati (popolati quando si conosce la durata)
    this.midrollPoints = [];
    
    console.log('📢 AdManager inizializzato:', {
      totalAds: this.adsConfig.length,
      prerolls: this.getPrerolls().length,
      midrolls: this.getMidrolls().length,
      postrolls: this.getPostrolls().length,
      frequencyCapping: this.frequencyCap.enabled
    });
  }
  
  /**
   * Parse e valida configurazione ads
   * 
   * @param {Array} adsConfig - Array configurazione ads
   * @returns {Array} Ads validati e normalizzati
   * @private
   */
  parseAndValidateAds(adsConfig) {
    if (!Array.isArray(adsConfig)) {
      console.warn('⚠️ ads config non è un array, usando array vuoto');
      return [];
    }
    
    return adsConfig.map((ad, index) => {
      // Genera ID se mancante
      if (!ad.id) {
        ad.id = `ad-${ad.type}-${index}-${generateId()}`;
      }
      
      // Valida type
      if (!Object.values(AD_TYPES).includes(ad.type)) {
        console.error(`❌ Ad type invalido: ${ad.type}`, ad);
        return null;
      }
      
      // Valida source
      if (!Object.values(AD_SOURCES).includes(ad.source)) {
        console.error(`❌ Ad source invalido: ${ad.source}`, ad);
        return null;
      }
      
      // Valida sorgente dati
      if (ad.source === AD_SOURCES.STATIC && !ad.audioUrl) {
        console.error('❌ Static ad senza audioUrl', ad);
        return null;
      }
      
      if (ad.source === AD_SOURCES.GAM && !ad.vastTag) {
        console.error('❌ GAM ad senza vastTag', ad);
        return null;
      }
      
      // Spreaker: gli ads sono già nel flusso audio, non serve configurazione aggiuntiva
      if (ad.source === AD_SOURCES.SPREAKER) {
        console.warn('⚠️ Spreaker ads sono già inclusi nello stream, ignorando configurazione ad esplicita');
        return null; // Non aggiungere ads espliciti per Spreaker
      }
      
      // Normalizza position per midroll
      if (ad.type === AD_TYPES.MIDROLL) {
        if (!ad.position) {
          console.error('❌ Midroll senza position', ad);
          return null;
        }
        
        // Normalizza format position
        if (typeof ad.position === 'number') {
          // Assume percentuale se < 100, altrimenti secondi
          ad.position = {
            type: ad.position <= 100 ? POSITION_TYPES.PERCENTAGE : POSITION_TYPES.SECONDS,
            value: ad.position
          };
        }
      }
      
      // Aggiungi defaults
      return {
        ...ad,
        state: AD_STATES.PENDING,
        skippable: ad.skippable ?? false,
        skipAfter: ad.skipAfter ?? 0, // Secondi dopo cui diventa skippabile
        fallback: ad.fallback ?? null  // Config ad di fallback
      };
    }).filter(Boolean); // Rimuovi ads invalidi
  }
  
  /**
   * Ottieni prerolls
   * @returns {Array} Array di preroll ads
   */
  getPrerolls() {
    return this.adsConfig.filter(ad => ad.type === AD_TYPES.PREROLL);
  }
  
  /**
   * Ottieni midrolls
   * @returns {Array} Array di midroll ads
   */
  getMidrolls() {
    return this.adsConfig.filter(ad => ad.type === AD_TYPES.MIDROLL);
  }
  
  /**
   * Ottieni postrolls
   * @returns {Array} Array di postroll ads
   */
  getPostrolls() {
    return this.adsConfig.filter(ad => ad.type === AD_TYPES.POSTROLL);
  }
  
  /**
   * Calcola posizioni midroll in secondi
   * 
   * @param {number} totalDuration - Durata totale contenuto in secondi
   */
  calculateMidrollPoints(totalDuration) {
    const midrolls = this.getMidrolls();
    
    this.midrollPoints = midrolls.map(ad => {
      let timeInSeconds;
      
      if (ad.position.type === POSITION_TYPES.PERCENTAGE) {
        timeInSeconds = (ad.position.value / 100) * totalDuration;
      } else {
        timeInSeconds = ad.position.value;
      }
      
      return {
        adId: ad.id,
        time: timeInSeconds,
        triggered: false
      };
    }).sort((a, b) => a.time - b.time); // Ordina per tempo
    
    console.log('📍 Midroll points calcolati:', this.midrollPoints);
  }
  
  /**
   * Controlla se c'è un midroll da riprodurre a questo timestamp
   * 
   * @param {number} currentTime - Tempo corrente in secondi
   * @returns {Object|null} Config ad da riprodurre o null
   */
  checkMidrollTrigger(currentTime) {
    for (const point of this.midrollPoints) {
      if (!point.triggered && currentTime >= point.time) {
        point.triggered = true;
        const ad = this.adsConfig.find(a => a.id === point.adId);
        return ad;
      }
    }
    return null;
  }
  
  /**
   * Verifica frequency capping per un ad
   * 
   * @param {string} adId - ID dell'ad
   * @returns {boolean} True se l'ad può essere riprodotto
   */
  canPlayAd(adId) {
    if (!this.frequencyCap.enabled) {
      return true; // Nessun capping
    }
    
    // Controlla se già riprodotto in questa sessione/periodo
    if (this.playedAds.has(adId)) {
      console.log('🚫 Ad già riprodotto (frequency cap):', adId);
      return false;
    }
    
    // Controlla storage persistente
    const storage = this.frequencyCap.storage === 'local' ? localStorage : sessionStorage;
    if (this.frequencyCap.storage !== 'none') {
      try {
        const key = `${this.frequencyCap.key}-${adId}`;
        const data = JSON.parse(storage.getItem(key) || '{}');
        
        if (data.plays >= this.frequencyCap.maxPlays) {
          // Controlla time window
          if (this.frequencyCap.timeWindow) {
            const elapsed = Date.now() - (data.firstPlay || 0);
            if (elapsed < this.frequencyCap.timeWindow) {
              console.log('🚫 Ad bloccato da frequency cap (time window):', adId);
              return false;
            }
          } else {
            console.log('🚫 Ad bloccato da frequency cap:', adId);
            return false;
          }
        }
      } catch (e) {
        console.warn('⚠️ Errore lettura frequency cap storage:', e);
      }
    }
    
    return true;
  }
  
  /**
   * Marca un ad come riprodotto (frequency capping + state sync)
   * 
   * @param {string} adId - ID dell'ad
   */
  markAdPlayed(adId) {
    this.playedAds.add(adId);
    
    // Sincronizza con StateManager se disponibile
    if (this.stateManager) {
      const { adsPlayed } = this.stateManager.getState();
      if (!adsPlayed.includes(adId)) {
        this.stateManager.setState({ adsPlayed: [...adsPlayed, adId] });
      }
    }
    
    if (!this.frequencyCap.enabled || this.frequencyCap.storage === 'none') {
      return;
    }
    
    const storage = this.frequencyCap.storage === 'local' ? localStorage : sessionStorage;
    const key = `${this.frequencyCap.key}-${adId}`;
    
    try {
      const data = JSON.parse(storage.getItem(key) || '{}');
      
      storage.setItem(key, JSON.stringify({
        plays: (data.plays || 0) + 1,
        firstPlay: data.firstPlay || Date.now(),
        lastPlay: Date.now()
      }));
      
      console.log('💾 Ad marcato come riprodotto:', adId);
    } catch (e) {
      console.warn('⚠️ Errore salvataggio frequency cap:', e);
    }
  }
  
  /**
   * Riproduce un ad
   * 
   * @param {Object} adConfig - Configurazione ad
   * @param {HTMLElement} hostElement - Elemento host per eventi
   * @returns {Promise<void>}
   */
  async playAd(adConfig, hostElement) {
    // Verifica frequency capping
    if (!this.canPlayAd(adConfig.id)) {
      console.log('⏭️ Ad skippato per frequency capping:', adConfig.id);
      return;
    }
    
    this.currentAd = adConfig;
    
    try {
      // Dispatch evento start
      dispatchCustomEvent(hostElement, AD_EVENTS.AD_START, {
        adId: adConfig.id,
        adType: adConfig.type,
        adSource: adConfig.source,
        adConfig
      });
      
      // Carica audio ad
      let audioUrl;
      let trackingEvents = {};
      
      if (adConfig.source === AD_SOURCES.STATIC) {
        audioUrl = adConfig.audioUrl;
      } else if (adConfig.source === AD_SOURCES.GAM) {
        const vastData = await this.vastParser.parse(adConfig.vastTag);
        audioUrl = vastData.mediaFile.url;
        trackingEvents = vastData.trackingEvents;
        
        // Fire impression tracking
        if (trackingEvents.impression) {
          VASTParser.fireTrackingArray(trackingEvents.impression);
        }
      }
      
      // Riproduci ad
      await this.playAdAudio(audioUrl, adConfig, trackingEvents, hostElement);
      
      // Marca come riprodotto
      this.markAdPlayed(adConfig.id);
      
      // Dispatch evento complete
      dispatchCustomEvent(hostElement, AD_EVENTS.AD_COMPLETE, {
        adId: adConfig.id,
        adType: adConfig.type,
        adSource: adConfig.source
      });
      
    } catch (error) {
      console.error('❌ Errore riproduzione ad:', error);
      
      // Dispatch evento error
      dispatchCustomEvent(hostElement, AD_EVENTS.AD_ERROR, {
        adId: adConfig.id,
        adType: adConfig.type,
        error: error.message
      });
      
      // Prova fallback se configurato
      if (adConfig.fallback) {
        console.log('🔄 Tentativo fallback ad...');
        await this.playAd(adConfig.fallback, hostElement);
      }
    } finally {
      this.currentAd = null;
    }
  }
  
  /**
   * Riproduce audio ad
   * 
   * @param {string} audioUrl - URL audio ad
   * @param {Object} adConfig - Config ad
   * @param {Object} trackingEvents - Eventi tracking VAST
   * @param {HTMLElement} hostElement - Host per eventi
   * @returns {Promise<void>}
   * @private
   */
  playAdAudio(audioUrl, adConfig, trackingEvents, hostElement) {
    return new Promise((resolve, reject) => {
      if (!this.adAudioElement) {
        this.adAudioElement = new Audio();
      }
      
      this.adAudioElement.src = audioUrl;
      this.adAudioElement.preload = 'auto';
      
      // Listeners
      const onEnded = () => {
        cleanup();
        
        // Fire complete tracking
        if (trackingEvents.complete) {
          VASTParser.fireTrackingArray(trackingEvents.complete);
        }
        
        resolve();
      };
      
      const onError = (e) => {
        cleanup();
        reject(new Error(`Ad playback error: ${e.message || 'Unknown'}`));
      };
      
      const onTimeUpdate = () => {
        const progress = (this.adAudioElement.currentTime / this.adAudioElement.duration) * 100;
        
        // Dispatch progresso
        dispatchCustomEvent(hostElement, AD_EVENTS.AD_PROGRESS, {
          adId: adConfig.id,
          currentTime: this.adAudioElement.currentTime,
          duration: this.adAudioElement.duration,
          progress
        });
        
        // Fire tracking quartili VAST
        if (trackingEvents.firstQuartile && progress >= 25 && progress < 50) {
          VASTParser.fireTrackingArray(trackingEvents.firstQuartile);
          delete trackingEvents.firstQuartile; // Fire una sola volta
        }
        if (trackingEvents.midpoint && progress >= 50 && progress < 75) {
          VASTParser.fireTrackingArray(trackingEvents.midpoint);
          delete trackingEvents.midpoint;
        }
        if (trackingEvents.thirdQuartile && progress >= 75 && progress < 100) {
          VASTParser.fireTrackingArray(trackingEvents.thirdQuartile);
          delete trackingEvents.thirdQuartile;
        }
      };
      
      const cleanup = () => {
        this.adAudioElement.removeEventListener('ended', onEnded);
        this.adAudioElement.removeEventListener('error', onError);
        this.adAudioElement.removeEventListener('timeupdate', onTimeUpdate);
      };
      
      this.adAudioElement.addEventListener('ended', onEnded);
      this.adAudioElement.addEventListener('error', onError);
      this.adAudioElement.addEventListener('timeupdate', onTimeUpdate);
      
      // Fire start tracking
      if (trackingEvents.start) {
        VASTParser.fireTrackingArray(trackingEvents.start);
      }
      
      // Play
      this.adAudioElement.play().catch(reject);
    });
  }
  
  /**
   * Riproduce sequenza di ads
   * 
   * @param {Array} ads - Array di ads da riprodurre
   * @param {HTMLElement} hostElement - Host per eventi
   * @returns {Promise<void>}
   */
  async playAdSequence(ads, hostElement) {
    for (const ad of ads) {
      if (this.canPlayAd(ad.id)) {
        await this.playAd(ad, hostElement);
      }
    }
  }
  
  /**
   * Riproduce tutti i prerolls
   * 
   * @param {HTMLElement} hostElement - Host per eventi
   * @returns {Promise<void>}
   */
  async playPrerolls(hostElement) {
    const prerolls = this.getPrerolls();
    if (prerolls.length > 0) {
      console.log('▶️ Riproduzione prerolls:', prerolls.length);
      await this.playAdSequence(prerolls, hostElement);
    }
  }
  
  /**
   * Riproduce tutti i postrolls
   * 
   * @param {HTMLElement} hostElement - Host per eventi
   * @returns {Promise<void>}
   */
  async playPostrolls(hostElement) {
    const postrolls = this.getPostrolls();
    if (postrolls.length > 0) {
      console.log('▶️ Riproduzione postrolls:', postrolls.length);
      await this.playAdSequence(postrolls, hostElement);
    }
  }
  
  /**
   * Reset stato ads (per replay)
   */
  reset() {
    this.playedAds.clear();
    this.midrollPoints.forEach(point => point.triggered = false);
    this.currentAd = null;
  }
  
  /**
   * Cleanup risorse
   */
  destroy() {
    if (this.adAudioElement) {
      this.adAudioElement.pause();
      this.adAudioElement.src = '';
      this.adAudioElement = null;
    }
    
    this.currentAd = null;
    this.playedAds.clear();
    this.midrollPoints = [];
  }
}

